import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { firestore } from '../../firebase/config';
import { initGoogleCalendarApi, authenticateWithGoogle } from '../../services/calendarSync';
import './PractitionerDashboard.css';
import ServiceManager from './ServiceManager';
import AvailabilityCalendar from './AvailabilityCalendar';
import AvailabilityManager from './AvailabilityManager';
import ProfileForm from './ProfileForm';
import AppointmentDetails from './AppointmentDetails';
// Ajouter l'import du composant de confirmation
import ConfirmationModal from '../Common/ConfirmationModal';
import emailService from '../../services/emailService';

const PractitionerDashboard = () => {
  const { currentUser, userProfile } = useContext(AuthContext);
  const [practitioner, setPractitioner] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [activeTab, setActiveTab] = useState('calendar');
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedView, setSelectedView] = useState('upcoming');
  // Nouvel état pour le mode d'affichage (calendrier ou liste)
  const [displayMode, setDisplayMode] = useState('calendar');

  // États pour les rappels
  const [reminderStatus, setReminderStatus] = useState({});
  const [sendingBatchReminders, setSendingBatchReminders] = useState(false);

  // Nouvel état pour le modal de confirmation d'annulation
  const [cancelConfirmation, setCancelConfirmation] = useState({
    isOpen: false,
    appointmentId: null,
    appointmentDetails: null
  });

  useEffect(() => {
    const fetchPractitionerData = async () => {
      try {
        if (userProfile?.practitionerId) {
          const practitionerDoc = await firestore
            .collection('practitioners')
            .doc(userProfile.practitionerId)
            .get();

          if (practitionerDoc.exists) {
            setPractitioner(practitionerDoc.data());
            setGoogleCalendarConnected(!!practitionerDoc.data().googleCalendarId);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Erreur lors de la récupération des données du praticien:', error);
        setLoading(false);
      }
    };

    fetchPractitionerData();
  }, [userProfile]);

  // Charger les rendez-vous
  const fetchAppointments = async () => {
    if (!userProfile?.practitionerId) return;

    try {
      console.log("Chargement des rendez-vous pour le praticien:", userProfile.practitionerId);

      const snapshot = await firestore
        .collection('appointments')
        .where('practitionerId', '==', userProfile.practitionerId)
        .get();

      const appointmentsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log("Rendez-vous chargés:", appointmentsList.length);

      // Log des dates de rendez-vous pour le debug
      appointmentsList.forEach(apt => {
        console.log(`Rendez-vous: ${apt.date} à ${apt.time}, statut: ${apt.status}`);
      });

      setAppointments(appointmentsList);
    } catch (error) {
      console.error('Erreur lors de la récupération des rendez-vous:', error);
    }
  };

  // Charger les rendez-vous au montage du composant
  useEffect(() => {
    fetchAppointments();
  }, [userProfile]);

  // Rafraîchir les rendez-vous lorsque le tab ou la vue change
  useEffect(() => {
    if (activeTab === 'calendar') {
      console.log("Rafraîchissement des rendez-vous pour la vue:", selectedView);
      fetchAppointments();
    }
  }, [activeTab, selectedView]);

  const connectGoogleCalendar = async () => {
    try {
      await initGoogleCalendarApi();
      const user = await authenticateWithGoogle();

      if (user && userProfile?.practitionerId) {
        // Mettre à jour le profil du praticien avec l'ID Google Calendar
        await firestore
          .collection('practitioners')
          .doc(userProfile.practitionerId)
          .update({
            googleCalendarId: user.getBasicProfile().getEmail()
          });

        setGoogleCalendarConnected(true);
      }
    } catch (error) {
      console.error('Erreur lors de la connexion à Google Calendar:', error);
    }
  };

  // Mettre à jour le praticien après modification du profil
  const handleProfileUpdate = (updatedPractitioner) => {
    setPractitioner(updatedPractitioner);
  };

  // Ouvrir le modal de détails d'un rendez-vous
  const openAppointmentDetails = (appointment) => {
    setSelectedAppointment(appointment);
  };

  // Fermer le modal de détails
  const closeAppointmentDetails = () => {
    setSelectedAppointment(null);
  };

  // Mettre à jour un rendez-vous dans la liste
  const handleAppointmentUpdate = (updatedAppointment) => {
    setAppointments(appointments.map(apt =>
      apt.id === updatedAppointment.id ? updatedAppointment : apt
    ));
  };

  // Modifier la fonction d'annulation pour ouvrir le modal de confirmation
  const handleCancelRequest = (appointment) => {
    // Ouvrir le modal de confirmation avec les détails du rendez-vous
    setCancelConfirmation({
      isOpen: true,
      appointmentId: appointment.id,
      appointmentDetails: appointment
    });
  };

  const handleSendReminder = async (appointment) => {
    if (!appointment || !appointment.id) return;

    // Vérifier si un rappel a déjà été envoyé récemment (dans les 24 dernières heures)
    if (reminderStatus[appointment.id] === 'pending') {
      alert('Un rappel est déjà en cours d\'envoi pour ce rendez-vous.');
      return;
    }

    if (appointment.reminderSent) {
      const confirmSend = window.confirm('Un rappel a déjà été envoyé pour ce rendez-vous. Souhaitez-vous en envoyer un autre?');
      if (!confirmSend) return;
    }

    try {
      // Marquer le rappel comme en cours d'envoi
      setReminderStatus(prev => ({
        ...prev,
        [appointment.id]: 'pending'
      }));

      console.log('Envoi d\'un rappel pour le rendez-vous:', appointment.id);

      // Envoyer le rappel
      emailService.sendReminderEmail(appointment).then(() => {
        setAppointments(prev =>
          prev.map(apt =>
            apt.id === appointment.id
              ? { ...apt, reminderSent: true, reminderSentAt: new Date() }
              : apt
          )
        );

        // Marquer le rappel comme envoyé
        setReminderStatus(prev => ({
          ...prev,
          [appointment.id]: 'sent'
        }));

        // Réinitialiser le statut après 3 secondes
        setTimeout(() => {
          setReminderStatus(prev => ({
            ...prev,
            [appointment.id]: null
          }));
        }, 3000);

        alert('Rappel envoyé avec succès !');
      });

      // Mettre à jour la liste des rendez-vous



    } catch (error) {
      console.error('Erreur lors de l\'envoi du rappel:', error);
      alert('Erreur lors de l\'envoi du rappel. Veuillez réessayer.');

      // Réinitialiser le statut en cas d'erreur
      setReminderStatus(prev => ({
        ...prev,
        [appointment.id]: 'error'
      }));
    }
  };

  const handleSendBatchReminders = async () => {
    // Calculer la date de demain
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    // Trouver tous les rendez-vous de demain qui sont confirmés
    const tomorrowAppointments = appointments.filter(apt =>
      apt.date === tomorrowStr &&
      apt.status === 'confirmed' &&
      !apt.reminderSent // N'inclure que ceux qui n'ont pas encore reçu de rappel
    );

    if (tomorrowAppointments.length === 0) {
      alert('Aucun rendez-vous à rappeler pour demain.');
      return;
    }

    const confirmSend = window.confirm(`Voulez-vous envoyer des rappels pour les ${tomorrowAppointments.length} rendez-vous prévus demain ?`);
    if (!confirmSend) return;

    try {
      setSendingBatchReminders(true);

      console.log(`Envoi de rappels pour ${tomorrowAppointments.length} rendez-vous`);

      // Envoyer les rappels un par un
      const results = await emailService.sendBatchReminderEmails(tomorrowAppointments);

      // Mettre à jour la liste des rendez-vous
      const updatedAppointments = [...appointments];
      for (const apt of tomorrowAppointments) {
        const index = updatedAppointments.findIndex(a => a.id === apt.id);
        if (index !== -1) {
          updatedAppointments[index] = {
            ...updatedAppointments[index],
            reminderSent: true,
            reminderSentAt: new Date()
          };
        }
      }
      setAppointments(updatedAppointments);

      alert(`${results.length} rappels ont été envoyés avec succès !`);
    } catch (error) {
      console.error('Erreur lors de l\'envoi des rappels en lot:', error);
      alert('Une erreur est survenue lors de l\'envoi des rappels. Veuillez réessayer.');
    } finally {
      setSendingBatchReminders(false);
    }
  };

  // Fonction d'annulation effective après confirmation
  const handleCancelAppointment = async () => {
    const { appointmentId } = cancelConfirmation;

    if (!appointmentId) return;

    try {
      await firestore
        .collection('appointments')
        .doc(appointmentId)
        .update({
          status: 'canceled',
          updatedAt: new Date(),
          canceledBy: 'practitioner'
        });

      // Mettre à jour la liste locale
      setAppointments(appointments.map(apt =>
        apt.id === appointmentId ? { ...apt, status: 'canceled', canceledBy: 'practitioner' } : apt
      ));

      // Fermer le modal après l'annulation
      setCancelConfirmation({
        isOpen: false,
        appointmentId: null,
        appointmentDetails: null
      });

    } catch (error) {
      console.error('Erreur lors de l\'annulation du rendez-vous:', error);
      alert('Une erreur est survenue lors de l\'annulation du rendez-vous.');
    }
  };

  // Fermer le modal de confirmation sans annuler
  const closeCancelConfirmation = () => {
    setCancelConfirmation({
      isOpen: false,
      appointmentId: null,
      appointmentDetails: null
    });
  };

  // Fonction pour générer le message de confirmation d'annulation
  const generateCancellationMessage = () => {
    const appointment = cancelConfirmation.appointmentDetails;
    if (!appointment) return null;

    // Calculer la différence en heures entre maintenant et le rendez-vous
    const appointmentDate = new Date(`${appointment.date}T${appointment.time}`);
    const now = new Date();
    const diffTime = appointmentDate - now;
    const diffHours = Math.round(diffTime / (1000 * 60 * 60));

    return (
      <>
        <p>Vous êtes sur le point d'annuler le rendez-vous suivant :</p>

        <div className="appointment-summary">
          <p><strong>Client :</strong> {appointment.name}</p>
          <p><strong>Service :</strong> {appointment.serviceTitle}</p>
          <p><strong>Date :</strong> {appointment.formattedDate}</p>
          <p><strong>Heure :</strong> {appointment.time}</p>
        </div>

        <div className="policy-section">
          <div className="policy-title">Politique d'annulation</div>
          <p>
            {diffHours > 24 ? (
              <>
                Ce rendez-vous est prévu dans plus de 24 heures. L'annulation n'entraînera pas de frais pour le client.
              </>
            ) : (
              <>
                <span className="highlight-text">Attention :</span> Ce rendez-vous est prévu dans moins de 24 heures.
                Selon votre politique d'annulation, le client pourrait avoir droit à un remboursement ou des frais d'annulation pourraient s'appliquer.
              </>
            )}
          </p>
          <p>Le client sera notifié de cette annulation par email.</p>
        </div>

        <p>Souhaitez-vous vraiment annuler ce rendez-vous ?</p>
      </>
    );
  };

  // Filtrer les rendez-vous pour un jour spécifique
  const getDayAppointments = (date) => {
    console.log(`Filtrage pour la date ${date}, vue ${selectedView}`);
    console.log(`Nombre total de rendez-vous: ${appointments.length}`);

    const filteredAppointments = appointments.filter(appointment => {
      // Vérifier que le rendez-vous a une date valide
      if (!appointment.date) {
        console.log("Rendez-vous sans date:", appointment);
        return false;
      }

      console.log(`Comparaison: date rendez-vous ${appointment.date} avec ${date}, statut ${appointment.status}`);

      // Si nous sommes dans la vue "canceled", on ne montre que les rendez-vous annulés
      if (selectedView === 'canceled') {
        return appointment.date === date && appointment.status === 'canceled';
      }
      // Sinon, on montre les rendez-vous confirmés
      return appointment.date === date && appointment.status === 'confirmed';
    });

    console.log(`Rendez-vous filtrés pour ${date}: ${filteredAppointments.length}`);

    return filteredAppointments.sort((a, b) => {
      return convertTimeToMinutes(a.time) - convertTimeToMinutes(b.time);
    });
  };

  // Filtrer les rendez-vous pour la vue liste
  const getFilteredAppointments = () => {
    console.log(`Filtrage pour la vue liste, type: ${selectedView}`);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return appointments.filter(appointment => {
      if (!appointment.date) return false;

      const appointmentDate = new Date(appointment.date);

      if (selectedView === 'upcoming') {
        return appointmentDate >= today && appointment.status === 'confirmed';
      } else if (selectedView === 'canceled') {
        return appointment.status === 'canceled';
      } else if (selectedView === 'past') {
        return appointmentDate < today && appointment.status === 'confirmed';
      }

      return true;
    }).sort((a, b) => {
      // Trier d'abord par date, puis par heure
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      if (dateA.getTime() !== dateB.getTime()) {
        // Pour les rendez-vous passés, trier par date décroissante
        if (selectedView === 'past') {
          return dateB - dateA;
        }
        // Pour les rendez-vous à venir, trier par date croissante
        return dateA - dateB;
      }

      // Si même date, trier par heure
      return convertTimeToMinutes(a.time) - convertTimeToMinutes(b.time);
    });
  };

  // Générer les jours de la semaine pour la vue agenda
  const generateWeekDays = () => {
    const days = [];
    const currentDate = new Date(selectedDate);
    const dayOfWeek = currentDate.getDay(); // 0 = dimanche, 1 = lundi, etc.

    // Déterminer le lundi de la semaine en cours
    const monday = new Date(currentDate);
    monday.setDate(currentDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    console.log(`Génération des jours de la semaine à partir de: ${monday.toISOString()}`);

    // Générer 7 jours à partir du lundi
    for (let i = 0; i < 7; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);

      // Format de date ISO YYYY-MM-DD pour la comparaison avec les rendez-vous
      const dateStr = day.toISOString().split('T')[0];

      days.push({
        date: dateStr,
        dayName: day.toLocaleDateString('fr-FR', { weekday: 'long' }),
        dayNumber: day.getDate(),
        month: day.toLocaleDateString('fr-FR', { month: 'short' }),
        isToday: day.toDateString() === new Date().toDateString()
      });

      console.log(`Jour généré: ${dateStr}`);
    }

    return days;
  };

  // Fonctions auxiliaires pour le calcul du temps
  // Convertir une heure au format HH:MM en minutes depuis minuit
  const convertTimeToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Convertir des minutes depuis minuit en heure au format HH:MM
  const minutesToTimeString = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Formatage de la durée pour l'affichage
  const formatDuration = (duration) => {
    if (!duration) return '';
    if (duration === 90) return '1h30';
    if (duration === 60) return '1h';
    if (duration === 45) return '45min';
    return `${duration} min`;
  };

  // Gestionnaire de changement de semaine
  const handleWeekChange = (direction) => {
    const currentDate = new Date(selectedDate);
    if (direction === 'prev') {
      currentDate.setDate(currentDate.getDate() - 7);
    } else {
      currentDate.setDate(currentDate.getDate() + 7);
    }
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  // Gestionnaire de sélection de jour
  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  // Formater la date pour l'affichage
  const formatDate = (dateString) => {
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  // Changer le mode d'affichage entre calendrier et liste
  const toggleDisplayMode = (mode) => {
    setDisplayMode(mode);
  };

  if (loading) {
    return <div className="loading">Chargement du tableau de bord...</div>;
  }

  return (
    <div className="practitioner-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Tableau de bord praticien</h1>
          <p>Gérez vos rendez-vous et votre disponibilité</p>
        </div>

        <div className="dashboard-actions">
          {!googleCalendarConnected ? (
            <button className="btn btn-primary" onClick={connectGoogleCalendar}>
              Connecter à Google Calendar (à venir)
            </button>
          ) : (
            <div className="google-calendar-connected">
              <span className="connected-icon">✓</span>
              <span>Google Calendar connecté</span>
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-tabs">
        <button
          className={`tab ${activeTab === 'calendar' && selectedView === 'upcoming' ? 'active' : ''}`}
          onClick={() => { setActiveTab('calendar'); setSelectedView('upcoming'); }}
        >
          Mes rendez-vous
        </button>
        <button
          className={`tab ${activeTab === 'absences' ? 'active' : ''}`}
          onClick={() => setActiveTab('absences')}
        >
          Absences
        </button>
        <button
          className={`tab ${activeTab === 'availability' ? 'active' : ''}`}
          onClick={() => setActiveTab('availability')}
        >
          Disponibilité
        </button>
        <button
          className={`tab ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
        >
          Services
        </button>
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Mon profil
        </button>
      </div>

      <div className="dashboard-content">
        {/* Vue calendrier - affichage principal des rendez-vous */}
        {activeTab === 'calendar' && (
          <div className="calendar-container">
            {/* Bouton d'envoi de rappels en lot */}
            {/* <div className="batch-reminders">
              <button
                className="btn btn-outline"
                onClick={handleSendBatchReminders}
                disabled={sendingBatchReminders}
              >
                {sendingBatchReminders ? 'Envoi en cours...' : 'Envoyer des rappels pour demain'}
              </button>
            </div> */}

            <div className="view-selector">
              <button
                className={`btn ${selectedView === 'upcoming' ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setSelectedView('upcoming')}
              >
                Rendez-vous confirmés
              </button>
              <button
                className={`btn ${selectedView === 'past' ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setSelectedView('past')}
              >
                Rendez-vous passés
              </button>
              <button
                className={`btn ${selectedView === 'canceled' ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setSelectedView('canceled')}
              >
                Rendez-vous annulés
              </button>
            </div>

            {/* Sélecteur du mode d'affichage */}
            <div className="display-mode-selector">
              <button
                className={`btn ${displayMode === 'calendar' ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => toggleDisplayMode('calendar')}
              >
                <i className="fas fa-calendar-alt"></i> Calendrier
              </button>
              <button
                className={`btn ${displayMode === 'list' ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => toggleDisplayMode('list')}
              >
                <i className="fas fa-list"></i> Liste
              </button>
            </div>

            {displayMode === 'calendar' && (
              <div className="calendar-header">
                <div className="calendar-nav">
                  <button className="btn-icon" onClick={() => handleWeekChange('prev')} title="Semaine précédente"><i className="fas fa-chevron-left"></i></button>
                  <button
                    className="btn btn-outline btn-today"
                    onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                    title="Revenir à la semaine courante"
                  >
                    Aujourd'hui
                  </button>
                  <button className="btn-icon" onClick={() => handleWeekChange('next')} title="Semaine suivante"><i className="fas fa-chevron-right"></i></button>
                </div>
                <h3>Semaine du {formatDate(generateWeekDays()[0].date)}</h3>
              </div>
            )}
            {/* Vue Calendrier */}
            {displayMode === 'calendar' && (
              <div className="week-calendar">
                <div className="week-days-header">
                  {generateWeekDays().map(day => (
                    <div
                      key={day.date}
                      className={`week-day-header ${day.isToday ? 'today' : ''}`}
                    >
                      <div className="day-name">{day.dayName}</div>
                      <div className="day-number">{day.dayNumber} {day.month}</div>
                    </div>
                  ))}
                </div>

                <div className="week-days-content">
                  {generateWeekDays().map(day => {
                    const dayAppointments = getDayAppointments(day.date);

                    return (
                      <div key={day.date} className={`week-day-column ${day.isToday ? 'today' : ''}`}>
                        <div className="day-date-indicator">
                          {day.dayNumber} {day.month}
                        </div>

                        {dayAppointments.length === 0 ? (
                          <div className="no-appointments-day">
                            <p>Aucun rendez-vous</p>
                          </div>
                        ) : (
                          <div className="day-appointments">
                            {dayAppointments.map(appointment => {
                              if (!appointment.time || !appointment.duration) {
                                console.log("Rendez-vous incomplet:", appointment);
                                return null;
                              }

                              // Calculer l'heure de fin du rendez-vous
                              const startTimeMinutes = convertTimeToMinutes(appointment.time);
                              const endTimeMinutes = startTimeMinutes + (appointment.duration || 60);
                              const endTime = minutesToTimeString(endTimeMinutes);

                              // Calculer l'heure de fin incluant le temps de repos
                              const hasRestTime = appointment.restTime && appointment.restTime > 0;
                              const endTimeWithRestMinutes = endTimeMinutes + (appointment.restTime || 0);
                              const endTimeWithRest = minutesToTimeString(endTimeWithRestMinutes);

                              return (
                                <div
                                  key={appointment.id}
                                  className={`appointment-card ${appointment.status === 'canceled' ? 'canceled' : ''} ${hasRestTime ? 'has-rest-time' : ''}`}
                                  onClick={() => openAppointmentDetails(appointment)}
                                >
                                  <div className="appointment-time">
                                    {appointment.time} - {endTime}
                                  </div>
                                  <div className="appointment-details">
                                    <div className="appointment-title">{appointment.serviceTitle}</div>
                                    <div className="appointment-client">{appointment.name}</div>
                                    {appointment.status === 'canceled' && (
                                      <div className="appointment-status">Annulé</div>
                                    )}
                                    {appointment.isRemote && (
                                      <div className="appointment-remote-tag">
                                        <span className="remote-icon">💻</span> À distance
                                      </div>
                                    )}
                                  </div>

                                  {/* Affichage amélioré du temps de repos */}
                                  {hasRestTime && (
                                    <div className="appointment-rest-container">
                                      <div className="appointment-rest-bar">
                                        <div
                                          className="appointment-service-time"
                                          style={{ width: `${(appointment.duration / (appointment.duration + appointment.restTime)) * 100}%` }}
                                        ></div>

                                      </div>
                                      <div className="appointment-rest-time">
                                        <span className="rest-indicator">Repos:</span> {endTime} - {endTimeWithRest}
                                      </div>
                                    </div>
                                  )}

                                  {/* Boutons d'action (rappel et annulation) */}
                                  {selectedView === 'upcoming' && appointment.status === 'confirmed' && (
                                    <div className="appointment-actions-row">
                                      {/* <button
                                        className={`btn-reminder ${reminderStatus[appointment.id] === 'sent' ? 'sent' : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSendReminder(appointment);
                                        }}
                                        disabled={reminderStatus[appointment.id] === 'pending'}
                                        title="Envoyer un rappel au client"
                                      >
                                        {reminderStatus[appointment.id] === 'pending' ? 'Envoi...' :
                                          reminderStatus[appointment.id] === 'sent' ? 'Envoyé ✓' :
                                            appointment.reminderSent ? 'Renvoyer' : 'Rappel'}
                                      </button> */}

                                      {appointment.reminderSent && !reminderStatus[appointment.id] && (
                                        <div className="reminder-tooltip">
                                          <span className="reminder-badge sent"></span>
                                          <span className="reminder-tooltip-text">
                                            Rappel envoyé le {new Date(appointment.reminderSentAt?.toDate()).toLocaleString('fr-FR')}
                                          </span>
                                        </div>
                                      )}

                                      <button
                                        className="btn-cancel-appointment"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCancelRequest(appointment);
                                        }}
                                        title="Annuler le rendez-vous"
                                      >
                                        ✕</button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Légende pour les types de temps */}
                <div className="calendar-legend">
                  <div className="legend-item">
                    <div className="legend-color service"></div>
                    <span className="legend-text">Durée de service</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color rest"></div>
                    <span className="legend-text">Temps de repos</span>
                  </div>
                </div>
              </div>
            )}

            {/* Vue Liste */}
            {displayMode === 'list' && (
              <div className="appointments-list-view">
                {getFilteredAppointments().length === 0 ? (
                  <div className="no-appointments">
                    <p>Aucun rendez-vous pour cette période.</p>
                  </div>
                ) : (
                  <div className="appointments-table-container">
                    <table className="appointments-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Heure</th>
                          <th>Service</th>
                          <th>Durée</th>
                          <th>Client</th>
                          <th>Contact</th>
                          <th>Statut</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredAppointments().map(appointment => {
                          // Calculer l'heure de fin du rendez-vous
                          const startTimeMinutes = convertTimeToMinutes(appointment.time);
                          const endTimeMinutes = startTimeMinutes + (appointment.duration || 60);
                          const endTime = minutesToTimeString(endTimeMinutes);

                          // Si temps de repos
                          const hasRestTime = appointment.restTime && appointment.restTime > 0;
                          const endTimeWithRestMinutes = endTimeMinutes + (appointment.restTime || 0);
                          const endTimeWithRest = minutesToTimeString(endTimeWithRestMinutes);

                          return (
                            <tr
                              key={appointment.id}
                              onClick={() => openAppointmentDetails(appointment)}
                              className={`appointment-row ${appointment.status === 'canceled' ? 'canceled' : ''}`}
                            >
                              <td>{formatDate(appointment.date)}</td>
                              <td>
                                {appointment.time} - {endTime}
                                {hasRestTime && (
                                  <div className="rest-time-indicator">
                                    <span className="rest-indicator">⏱</span> Repos {endTime} - {endTimeWithRest} (+{appointment.restTime}min)
                                  </div>
                                )}
                              </td>
                              <td>
                                {appointment.serviceTitle}
                                {appointment.isRemote && (
                                  <span className="table-remote-badge">
                                    <span className="remote-icon">💻</span> À distance
                                  </span>
                                )}
                              </td>
                              <td>{formatDuration(appointment.duration)}</td>
                              <td>{appointment.name}</td>
                              <td>
                                <div>{appointment.phone}</div>
                                <div>{appointment.email}</div>
                              </td>
                              <td>
                                <span className={`status-badge ${appointment.status}`}>
                                  {appointment.status === 'confirmed' ? 'Confirmé' :
                                    appointment.status === 'canceled' ? 'Annulé' :
                                      appointment.status === 'completed' ? 'Terminé' :
                                        appointment.status}
                                </span>
                                {appointment.status === 'canceled' && appointment.canceledBy && (
                                  <div className="canceled-by">
                                    par {appointment.canceledBy === 'practitioner' ? 'vous' : 'le client'}
                                  </div>
                                )}
                              </td>
                              <td className="appointment-actions">
                                <button
                                  className="btn-icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAppointmentDetails(appointment);
                                  }}
                                  title="Voir détails"
                                >
                                  <i className="fas fa-eye"></i>
                                </button>

                                {selectedView === 'upcoming' && appointment.status === 'confirmed' && (
                                  <>
                                    {/* <button
                                      className={`btn-icon ${appointment.reminderSent ? 'btn-success' : 'btn-info'}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSendReminder(appointment);
                                      }}
                                      disabled={reminderStatus[appointment.id] === 'pending'}
                                      title={appointment.reminderSent ? 'Renvoyer un rappel' : 'Envoyer un rappel'}
                                    >
                                      {reminderStatus[appointment.id] === 'pending' ?
                                        <i className="fas fa-spinner fa-spin"></i> :
                                        <i className="fas fa-bell"></i>}
                                    </button> */}

                                    <button
                                      className="btn-icon btn-danger"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelRequest(appointment);
                                      }}
                                      title="Annuler rendez-vous"
                                    >
                                      <i className="fas fa-times"></i>
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Onglet Profil */}
        {activeTab === 'profile' && (
          <div className="profile-management">
            <h3>Modifier vos informations personnelles</h3>
            <p className="tab-description">
              Mettez à jour vos informations professionnelles et votre adresse qui seront visibles par vos clients.
            </p>
            <ProfileForm
              practitioner={practitioner}
              practitionerId={userProfile.practitionerId}
              onProfileUpdate={handleProfileUpdate}
            />
          </div>
        )}

        {/* Onglet Absences - gardé tel quel comme demandé */}
        {activeTab === 'absences' && (
          <div className="calendar-management">
            <AvailabilityCalendar />
          </div>
        )}

        {/* Onglet disponibilité */}
        {activeTab === 'availability' && (
          <div className="availability-management">
            <h3>Gérer vos disponibilités hebdomadaires</h3>
            <p className="tab-description">
              Définissez vos horaires par défaut pour chaque jour de la semaine. Pour gérer des disponibilités spécifiques ou des absences, utilisez l'onglet "Absences".
            </p>
            <AvailabilityManager />
          </div>
        )}

        {/* Onglet services */}
        {activeTab === 'services' && (
          <div className="services-management">
            <h3>Gérer vos services</h3>
            <ServiceManager practitionerId={userProfile.practitionerId} />
          </div>
        )}
      </div>

      {/* Modal de détails du rendez-vous */}
      {selectedAppointment && (
        <AppointmentDetails
          appointment={selectedAppointment}
          onClose={closeAppointmentDetails}
          onUpdate={handleAppointmentUpdate}
        />
      )}

      {/* Modal de confirmation d'annulation */}
      <ConfirmationModal
        isOpen={cancelConfirmation.isOpen}
        onClose={closeCancelConfirmation}
        onConfirm={handleCancelAppointment}
        title="Confirmation d'annulation"
        message={generateCancellationMessage()}
        cancelText="Revenir"
        confirmText="Confirmer l'annulation"
      />
    </div>
  );
};

export default PractitionerDashboard;